import { SerializedError } from "@reduxjs/toolkit";
import { BaseQueryFn } from "@reduxjs/toolkit/dist/query";
import { AxiosRequestConfig } from "axios";

import { isObject } from "@picovico/utils";
import axiosService, { AxiosCustomError } from "./lib/axios";
import { Table } from "@tanstack/table-core/build/lib/types";
import { round } from "lodash";

type axiosBaseQueryErrorType = {
  status?: number;
  message?: string;
  data?: AxiosCustomError["data"];
};

export function isErrorWithData(
  error: axiosBaseQueryErrorType | SerializedError,
): error is axiosBaseQueryErrorType {
  return !!(isObject(error) && "data" in error);
}

export type axiosBaseQueryFn = BaseQueryFn<
  {
    name: string;
    url?: string;
    method: AxiosRequestConfig["method"];
    data?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
    silentFeatureDisabled?: boolean;
  },
  unknown,
  axiosBaseQueryErrorType
>;

export const axiosBaseQuery =
  ({ keepStatus = false }: { keepStatus?: boolean } = {}): axiosBaseQueryFn =>
  async ({ method, url, data, params, name, ...extra }) => {
    try {
      const result = await axiosService({
        name,
        url,
        method,
        data,
        params,
        ...extra,
      });
      const contentType = result.headers["content-type"];
      if (!contentType?.includes("application/json")) return { data: result };
      return { data: result.data };
    } catch (axiosError) {
      const failure = axiosError as AxiosCustomError;
      // With `keepStatus`, an answer that has a status but no body (a bare 500, an HTML
      // error page) stays an error carrying its status. Without a status there was no
      // answer at all, and the callers that care word that as a network failure.
      if (keepStatus && !failure?.data && typeof failure?.status === "number") {
        return { error: { status_code: failure.status } };
      }
      return { error: failure?.data };
    }
  };

export type createAxiosBaseQuery = (args: {
  name: string;
  url?: string;
  method: AxiosRequestConfig["method"];
  data?: AxiosRequestConfig["data"];
  params?: AxiosRequestConfig["params"];
  idx?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) => Promise<any>;

export const createAxiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = {
      baseUrl: process.env.NEXT_PUBLIC_BASE_API_URL || "",
    },
  ): createAxiosBaseQuery =>
  async ({ method, url, data, params, name, idx, ...extra }) => {
    try {
      const result = await axiosService({
        baseURL: baseUrl,
        name,
        url,
        method,
        data,
        params,
        idx,
        ...extra,
      });
      return result.data;
    } catch (axiosError) {
      const err = axiosError as AxiosCustomError;
      const errorResponseData = err?.data;
      if (!errorResponseData) {
        throw new Error(err?.message);
      }
      return errorResponseData;
    }
  };

export const getColumnSum = <T> (table: Table<T>, columnId: string) => {
  const sum = table
    .getFilteredRowModel()
    .rows.reduce((acc, row) => acc + (Number(row.getValue(columnId)) || 0), 0);
  
  return round(sum, 2);
};

export const generateColors = (count: number) => {
  const colors = [];
  const hueStep = 360 / count;

  for (let i = 0; i < count; i++) {
    const hue = Math.round(i * hueStep);
    colors.push(`hsl(${hue}, 85%, 55%)`);
  }
  return colors;
}